export const ROLES = {
  SUPER_ADMIN: 'super_admin',
  TENANT_ADMIN: 'tenant_admin',
  TENANT_MANAGER: 'tenant_manager',
  TENANT_VIEWER: 'tenant_viewer',
} as const

export type Role = (typeof ROLES)[keyof typeof ROLES]

// Writers can mutate non-admin resources (recommendations, sync, M365 tenants, settings).
// Viewer is the only authenticated role excluded.
export const WRITER_ROLES: Role[] = ['super_admin', 'tenant_admin', 'tenant_manager']

export const TENANT_PLANS = {
  TRIAL: 'trial',
  STARTER: 'starter',
  PRO: 'pro',
  ENTERPRISE: 'enterprise',
} as const

export type TenantPlan = (typeof TENANT_PLANS)[keyof typeof TENANT_PLANS]

export const TENANT_STATUS = {
  ACTIVE: 'active',
  SUSPENDED: 'suspended',
  CANCELLED: 'cancelled',
} as const

export type TenantStatus = (typeof TENANT_STATUS)[keyof typeof TENANT_STATUS]

export const M365_TENANT_STATUS = {
  PENDING: 'pending',
  ACTIVE: 'active',
  ERROR: 'error',
  DISCONNECTED: 'disconnected',
} as const

export type M365TenantStatus = (typeof M365_TENANT_STATUS)[keyof typeof M365_TENANT_STATUS]

export const SYNC_STATUS = {
  QUEUED: 'queued',
  RUNNING: 'running',
  SUCCESS: 'success',
  PARTIAL: 'partial',
  FAILED: 'failed',
} as const

export type SyncStatus = (typeof SYNC_STATUS)[keyof typeof SYNC_STATUS]

export const RECOMMENDATION_TYPE = {
  REMOVE_LICENSE: 'remove_license',
  DOWNGRADE: 'downgrade',
  DISABLE_SERVICE: 'disable_service',
  REASSIGN: 'reassign',
  REDUCE_PURCHASED: 'reduce_purchased',
  REMOVE_REDUNDANT: 'remove_redundant',
  ALERT: 'alert',
  // Alertas de infrautilización por servicio (reglas de "Optimización avanzada")
  ALERT_EXCHANGE: 'alert_exchange',
  ALERT_ONEDRIVE: 'alert_onedrive',
  ALERT_SHAREPOINT: 'alert_sharepoint',
  ALERT_TEAMS: 'alert_teams',
  ALERT_COPILOT: 'alert_copilot',
} as const

export type RecommendationType =
  (typeof RECOMMENDATION_TYPE)[keyof typeof RECOMMENDATION_TYPE]

export const RECOMMENDATION_SEVERITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical',
} as const

export type RecommendationSeverity =
  (typeof RECOMMENDATION_SEVERITY)[keyof typeof RECOMMENDATION_SEVERITY]

export const RECOMMENDATION_STATUS = {
  PENDING: 'pending',
  ACCEPTED: 'accepted',
  DISMISSED: 'dismissed',
  APPLIED: 'applied',
  EXPIRED: 'expired',
  RESOLVED: 'resolved',
} as const

export type RecommendationStatus =
  (typeof RECOMMENDATION_STATUS)[keyof typeof RECOMMENDATION_STATUS]

// ─── Email notifications ───────────────────────────────────

/**
 * Dominio de envío dado de alta en Cloudflare Email Service. El remitente
 * configurado por tenant debe pertenecer a este dominio; Cloudflare rechaza
 * cualquier otro.
 */
export const EMAIL_SENDING_DOMAIN = 'no-reply.grupocic.es'
export const DEFAULT_EMAIL_FROM_NAME = 'ROI365'
export const DEFAULT_EMAIL_FROM_ADDRESS = `roi365@${EMAIL_SENDING_DOMAIN}`

/** Etiquetas de tipo/severidad compartidas por la UI y los correos. */
export const RECOMMENDATION_TYPE_LABELS: Record<RecommendationType, string> = {
  remove_license: 'Eliminar licencia',
  downgrade: 'Downgrade',
  disable_service: 'Deshabilitar servicio',
  reassign: 'Reasignar',
  reduce_purchased: 'Reducir compradas',
  remove_redundant: 'Eliminar redundante',
  alert: 'Alerta',
  alert_exchange: 'Infrautilización Exchange',
  alert_onedrive: 'Infrautilización OneDrive',
  alert_sharepoint: 'Infrautilización SharePoint',
  alert_teams: 'Infrautilización Teams',
  alert_copilot: 'Copilot sin uso',
}

export const RECOMMENDATION_SEVERITY_LABELS: Record<RecommendationSeverity, string> = {
  low: 'Baja',
  medium: 'Media',
  high: 'Alta',
  critical: 'Crítica',
}

/** Máximo de destinatarios del resumen de recomendaciones por tenant. */
export const MAX_NOTIFICATION_RECIPIENTS = 20

/** Orden de severidades para "severidad mínima" (inclusive). */
export const RECOMMENDATION_SEVERITY_ORDER: RecommendationSeverity[] = ['low', 'medium', 'high', 'critical']

export const USER_STATUS = {
  ACTIVE: 'active',
  INVITED: 'invited',
  DISABLED: 'disabled',
} as const

export type UserStatus = (typeof USER_STATUS)[keyof typeof USER_STATUS]

// ─── AI Recommendations ────────────────────────────────────

export const AI_RECOMMENDATION_CATEGORY = {
  COST_OPTIMIZATION: 'cost_optimization',
  ADOPTION: 'adoption',
  SECURITY: 'security',
  COMPLIANCE: 'compliance',
} as const

export type AiRecommendationCategory =
  (typeof AI_RECOMMENDATION_CATEGORY)[keyof typeof AI_RECOMMENDATION_CATEGORY]

export const AI_RECOMMENDATION_ORIGIN = {
  RULE: 'rule',
  AI_ENRICHED: 'ai_enriched',
  AI_GENERATED: 'ai_generated',
} as const

export type AiRecommendationOrigin =
  (typeof AI_RECOMMENDATION_ORIGIN)[keyof typeof AI_RECOMMENDATION_ORIGIN]

export const AI_RECOMMENDATION_STATUS = {
  DRAFT: 'draft',
  PENDING_REVIEW: 'pending_review',
  APPROVED: 'approved',
  PUBLISHED: 'published',
  DISMISSED: 'dismissed',
  APPLIED: 'applied',
  EXPIRED: 'expired',
  RESOLVED: 'resolved',
  /** Rejected by a ROI365 administrator in moderation (not a client decision). */
  REJECTED_ADMIN: 'rejected_admin',
  /** Historical rule-engine rows kept for audit; never shown to clients. */
  SUPERSEDED: 'superseded',
} as const

export type AiRecommendationStatus =
  (typeof AI_RECOMMENDATION_STATUS)[keyof typeof AI_RECOMMENDATION_STATUS]

export const AI_RECOMMENDATION_VISIBILITY = {
  PUBLIC: 'public',
  INTERNAL_ONLY: 'internal_only',
  HIDDEN: 'hidden',
} as const

export type AiRecommendationVisibility =
  (typeof AI_RECOMMENDATION_VISIBILITY)[keyof typeof AI_RECOMMENDATION_VISIBILITY]

export const AI_CONTEXT_SCOPE = {
  GLOBAL: 'global',
  TENANT: 'tenant',
  RECOMMENDATION_TYPE: 'recommendation_type',
} as const

export type AiContextScope =
  (typeof AI_CONTEXT_SCOPE)[keyof typeof AI_CONTEXT_SCOPE]

export const AI_CONTEXT_CATEGORY = {
  OPERATIONAL: 'operational',
  COMMERCIAL: 'commercial',
  OUTPUT_RESTRICTION: 'output_restriction',
} as const

export type AiContextCategory =
  (typeof AI_CONTEXT_CATEGORY)[keyof typeof AI_CONTEXT_CATEGORY]

export const AI_TENANT_VISIBILITY_MODE = {
  ALL: 'all',
  APPROVED_ONLY: 'approved_only',
  NONE: 'none',
} as const

export type AiTenantVisibilityMode =
  (typeof AI_TENANT_VISIBILITY_MODE)[keyof typeof AI_TENANT_VISIBILITY_MODE]

export const AI_TENANT_STRATEGY = {
  AUTOMATIC: 'automatic',
  CONSERVATIVE: 'conservative',
  AGGRESSIVE: 'aggressive',
  HIGH_CONFIDENCE_ONLY: 'high_confidence_only',
} as const

export type AiTenantStrategy =
  (typeof AI_TENANT_STRATEGY)[keyof typeof AI_TENANT_STRATEGY]

export const AI_GENERATION_FREQUENCY = {
  DAILY: 'daily',
  ON_DEMAND: 'on_demand',
  ON_SYNC: 'on_sync',
} as const

export type AiGenerationFrequency =
  (typeof AI_GENERATION_FREQUENCY)[keyof typeof AI_GENERATION_FREQUENCY]

export const AI_GENERATION_TRIGGER = {
  CRON: 'cron',
  MANUAL: 'manual',
  ON_SYNC: 'on_sync',
} as const

export type AiGenerationTrigger =
  (typeof AI_GENERATION_TRIGGER)[keyof typeof AI_GENERATION_TRIGGER]

export const AI_GENERATION_STATUS = {
  QUEUED: 'queued',
  RUNNING: 'running',
  COMPLETED: 'completed',
  FAILED: 'failed',
  PARTIAL: 'partial',
} as const

export type AiGenerationStatus =
  (typeof AI_GENERATION_STATUS)[keyof typeof AI_GENERATION_STATUS]

export const AI_MODERATION_STATUS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  EDITED: 'edited',
} as const

export type AiModerationStatus =
  (typeof AI_MODERATION_STATUS)[keyof typeof AI_MODERATION_STATUS]

// ─── AI Research (OpenAI Agents API) ───────────────────────

/** Kinds of opportunity a research task investigates. */
export const AI_RESEARCH_KIND = {
  SKU_OVERLAP: 'sku_overlap',
  SKU_ALTERNATIVE: 'sku_alternative',
  PREMIUM_UNDERUSE: 'premium_underuse',
  CAPABILITY_GAP: 'capability_gap',
  CATALOG_CHANGE: 'catalog_change',
  RULE_EXTENSION: 'rule_extension',
  SYNTHETIC_TEST: 'synthetic_test',
} as const

export type AiResearchKind = (typeof AI_RESEARCH_KIND)[keyof typeof AI_RESEARCH_KIND]

/** How a finding relates to the traditional rules engine. */
export const AI_FINDING_KIND = {
  NEW: 'new',
  EXTENSION: 'extension',
  CONFIRMATION: 'confirmation',
  DATA_GAP: 'data_gap',
} as const

export type AiFindingKind = (typeof AI_FINDING_KIND)[keyof typeof AI_FINDING_KIND]

export const AI_SAVING_STATUS = {
  COMPUTED: 'computed',
  ESTIMATED_CATALOG: 'estimated_catalog',
  PENDING_VALUATION: 'pending_valuation',
  NOT_APPLICABLE: 'not_applicable',
} as const

export type AiSavingStatus = (typeof AI_SAVING_STATUS)[keyof typeof AI_SAVING_STATUS]

export const AI_RESEARCH_TASK_STATUS = {
  PENDING: 'pending',
  RUNNING: 'running',
  COMPLETED: 'completed',
  NEEDS_REVIEW: 'needs_review',
  REJECTED: 'rejected',
  ERROR: 'error',
  CANCELLED: 'cancelled',
  SKIPPED_CAP: 'skipped_cap',
  SKIPPED_POLICY: 'skipped_policy',
  SKIPPED_BUDGET: 'skipped_budget',
} as const

export type AiResearchTaskStatus = (typeof AI_RESEARCH_TASK_STATUS)[keyof typeof AI_RESEARCH_TASK_STATUS]

/** Billing plan of a contracted license: commitment + payment cadence. */
export const LICENSE_BILLING_PLANS = ['monthly', 'annual_monthly', 'annual_upfront'] as const
export type LicenseBillingPlan = (typeof LICENSE_BILLING_PLANS)[number]
export const LICENSE_BILLING_PLAN_INFO: Record<LicenseBillingPlan, { label: string; commitment: string; payment: string }> = {
  monthly: { label: 'Mensual', commitment: 'Un mes', payment: 'Cada mes' },
  annual_monthly: { label: 'Anual con pago mensual', commitment: 'Un año', payment: 'Cada mes' },
  annual_upfront: { label: 'Anual con pago anual', commitment: 'Un año', payment: 'Por adelantado' },
}

export const AI_RESEARCH_ACTIONS = [
  'downgrade',
  'remove_license',
  'remove_redundant',
  'reassign',
  'disable_service',
  'renegotiate',
  'review',
  'validate_with_client',
  'none',
] as const

export type AiResearchAction = (typeof AI_RESEARCH_ACTIONS)[number]

export const AI_CONTEXT_MAX_LENGTH = 10000

export const GRAPH_API_BASE = 'https://graph.microsoft.com/v1.0'
export const GRAPH_API_BETA_BASE = 'https://graph.microsoft.com/beta'
export const GRAPH_API_BATCH_LIMIT = 20
export const GRAPH_API_PAGE_SIZE = 999

export const JWT_ACCESS_TTL_SECONDS = 900 // 15 minutes
export const JWT_REFRESH_TTL_DAYS = 30
export const SESSION_IDLE_TIMEOUT_SECONDS = 1800 // 30 minutes
export const RATE_LIMIT_API = 100 // requests per minute per tenant
export const RATE_LIMIT_AUTH = 10 // requests per minute per IP
